import {
  CalendarContext,
  EmployeeApi,
  environment,
  HolidayActionKind,
  IntroDialog,
  ManagerApi,
  PageCircularProgress,
  PortalModeType,
  ReducerContext,
  useAbdooReducer,
  useFeatures,
  useHoliday,
  useManager,
  UserSettingsActionKind,
  useSettings,
} from '@abdoo/common';
import { Box } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Route, Switch } from 'react-router-dom';

import { AddAbsence } from './AddAbsence/AddAbsence';
import { AppRoute } from './App/AppRoute';
import { BookingList } from './BookingList/BookingList';
import { Calendar } from './calendar/Calendar';
import { Home } from './Home/Home';
import { Navbar } from './Navbar/Navbar';
import { NotFoundComponent } from './NotFound/NotFoundComponent';
import { SettingsPage } from './Settings/SettingsPage';
import { StartManaging } from './StartManaging/StartManaging';

export function Routes() {
  const [calendarErrorMessage, setCalendarErrorMessage] = useState('');
  const { mode } = useManager(ManagerApi);
  const { featureFlags } = useFeatures(EmployeeApi);
  const { holidayBookings } = useHoliday(EmployeeApi);
  const { userSettings, holidayYearSummary, freeDays } = useSettings(EmployeeApi);
  const reducerContextObject = useAbdooReducer();
  const { userSettingsDispatch, holidayDispatch, userSettingsState } =
    reducerContextObject;

  useEffect(() => {
    if (userSettings.isSuccess && userSettings.data) {
      userSettingsDispatch({
        type: UserSettingsActionKind.UpdateHolidayYear,
        payload: userSettings.data,
      });
    }
  }, [userSettings.data, userSettings.isSuccess, userSettingsDispatch]);

  useEffect(() => {
    if (holidayBookings.isSuccess && holidayBookings.data) {
      holidayDispatch({
        type: HolidayActionKind.InitialiseHolidayBookings,
        payload: holidayBookings.data,
      });
    }
  }, [holidayBookings.data, holidayDispatch, holidayBookings.isSuccess]);

  useEffect(() => {
    if (holidayBookings.isError) {
      const error = holidayBookings.error as Error;
      setCalendarErrorMessage(error.message ?? 'Cannot get holiday bookings.');
    }
  }, [holidayBookings.error, holidayBookings.isError]);

  const onSubmitHolidayBooking = useCallback(() => {
    holidayYearSummary.refetch();
    holidayBookings.refetch();
    userSettings.refetch();
  }, [holidayYearSummary, holidayBookings, userSettings]);

  const onSubmitHolidayYearForm = useCallback(() => {
    holidayYearSummary.refetch();
    userSettings.refetch();
    freeDays.refetch();
    holidayBookings.refetch();
    userSettingsDispatch({ type: UserSettingsActionKind.RefreshUserData });
  }, [userSettingsDispatch, freeDays, holidayBookings, holidayYearSummary, userSettings]);

  useEffect(() => {
    // last used portal feature for production only.
    // change production to development to test the manager auto switching to last used portal locally.
    // reason for feature only existing for production mode:
    // causes issues when logging out as manager and then logging in as different user that is an employee only.
    // the manager still has a session in the employee portal, so it tries to switch to manager portal but fails due to new employee login
    // and also fails in employee portal due to current manager session still existing in employee portal
    // development has two different ports (two sessions for same user) for both portals while production only has one domain

    if (
      !mode.isLoading &&
      mode.data?.portalMode === PortalModeType.Manager &&
      process.env.REACT_APP_ENVIRONMENT === 'production'
    ) {
      window.location.href = environment.managerFrontendUrl ?? '';
    }
  }, [mode.isLoading, mode.data]);

  const isLoading =
    userSettings.isLoading ||
    holidayBookings.isLoading ||
    freeDays.isLoading ||
    holidayYearSummary.isLoading;

  return mode.isLoading || featureFlags.isLoading || userSettings.isLoading ? (
    <PageCircularProgress />
  ) : (
    <ReducerContext.Provider value={reducerContextObject}>
      <CalendarContext.Provider
        value={{
          calendarSummaryValues: holidayYearSummary.data ?? null,
          refreshCalendarSummary: holidayYearSummary.refetch,
          onSubmitHolidayYearForm,
          freeDays: freeDays.data ?? [],
          refreshHolidayBookings: holidayBookings.refetch,
          onSubmitHolidayBooking,
        }}
      >
        <Navbar />
        <Box sx={{ paddingTop: '60px' }}>
          <Switch>
            <AppRoute title="Calendar" path="/calendar">
              <Calendar
                isLoading={isLoading}
                isPlannerLoading={holidayBookings.isLoading}
                plannerError={calendarErrorMessage}
              />
            </AppRoute>
            <AppRoute title="Add Absence" path="/addAbsence">
              <AddAbsence
                isLoading={isLoading}
                isPlannerLoading={holidayBookings.isLoading}
                plannerError={calendarErrorMessage}
              />
            </AppRoute>
            <AppRoute title="History" path="/history">
              <BookingList />
            </AppRoute>
            <AppRoute title="Settings" path="/settings">
              <SettingsPage />
            </AppRoute>
            <AppRoute title={'Start Managing'} path="/startManaging">
              <StartManaging />
            </AppRoute>
            <AppRoute title="Employee" path={['/', '/home']}>
              <Home showChart={true} />
            </AppRoute>
            <Route path="*" component={NotFoundComponent} />
          </Switch>
        </Box>
        <IntroDialog open={!userSettingsState.initialSetupDone} type="fre" />
      </CalendarContext.Provider>
    </ReducerContext.Provider>
  );
}
